import { atom } from 'jotai';

/** Mirrors order-handler src/data/interfaces/inspo.ts. */
export type InspoConsent = 'featured' | 'anonymous' | 'hidden';
export type InspoJourney = 'store' | 'artist';

export interface InspoBestSeller {
    design_id: number;
    title: string;
    units: number;
    image_url: string | null;
    mockup_url: string | null;
    mockup_thumb_url: string | null;
}

export interface InspoMilestone {
    kind: 'first_sale' | 'first_10k' | 'best_month';
    label: string;
    index: number;
}

export interface InspoStory {
    key: string;
    consent: 'featured' | 'anonymous';
    journey: InspoJourney;
    name: string;
    founder_name: string | null;
    portrait_url: string | null;
    /** The brand's own photos; the first is the story cover. */
    photo_urls: string[];
    logo_url: string | null;
    /** The wide logo, shown large on a white tile. */
    wide_logo_url: string | null;
    storefront_url: string | null;
    headline: string;
    story: string | null;
    quote: string | null;
    quote_by: string | null;
    motif: string | null;
    started_label: string | null;
    figures: {
        monthly_now: string;
        best_month: string;
        customers: string;
        repeat_rate: number;
        months_to_10k: number | null;
        designs: string;
    };
    hero_designs: { titles: string[]; share_pct: number } | null;
    /** money: real monthly sales rounded to $1k (featured only). setup_until: first month after Wallmates' setup, -1 if none. */
    series: { labels: string[]; index: number[]; money: number[] | null; setup_until: number };
    milestones: InspoMilestone[];
    best_sellers: InspoBestSeller[];
}

export interface InspoPlaybook {
    hero_share_pct: number | null;
    top_motif: string | null;
    repeat_rate_range: [number, number] | null;
    typical_kept_pct: number | null;
}

export interface InspoHouse {
    name: string;
    logo_url: string | null;
    customers: string;
    orders: string;
}

export interface InspoPayload {
    generated_at: string;
    hero: InspoStory | null;
    featured: InspoStory[];
    rising: InspoStory | null;
    playbook: InspoPlaybook;
    house: InspoHouse | null;
}

export interface InspoFeatureRow {
    brand_id: number;
    consent: InspoConsent;
    journey: InspoJourney;
    headline?: string | null;
    story?: string | null;
    quote?: string | null;
    quote_by?: string | null;
    founder_name?: string | null;
    portrait_url?: string | null;
    photo_urls?: string[] | null;
    pinned?: boolean;
    consent_recorded_at?: string | null;
    consent_note?: string | null;
}

export type InspoFeatureInput = Partial<Omit<InspoFeatureRow, 'brand_id' | 'consent_recorded_at'>>;

export interface InspoCandidate {
    brand_id: number;
    name: string;
    sales_90d: number;
    sales_lifetime: number;
    customers: number;
    months_to_10k: number | null;
    feature: InspoFeatureRow | null;
}

export const inspoPayloadAtom = atom<InspoPayload | null>(null);
export const inspoLoadingAtom = atom<boolean>(false);
export const inspoFailedAtom = atom<boolean>(false);
